// ============================================================================
// AffectedJobsPanel Component
// ============================================================================
// Shared component for displaying affected jobs in wizard issue resolution steps

import { useRef, useState, useEffect } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import {
	Visibility,
	VisibilityOff,
	Delete,
	InsertDriveFileOutlined,
	CallMerge,
	ScatterPlot,
	GridOn,
} from "@mui/icons-material";
import { BOUNDARY_COLORS } from "../../shared/constants";
import type { Job } from "../../../../../../../models";
import "./AffectedJobsPanel.css";
import { PortalTooltip } from "../../../../../../helpers";

// ============================================================================
// Types
// ============================================================================

export interface AffectedJobsPanelProps {
	/** List of jobs affected by the current issue */
	jobs: Job[];
	/** Set of job IDs that are currently hidden on the map */
	hiddenJobIds: Set<string>;
	/** Toggle visibility of a single job */
	onToggleVisibility: (jobId: string) => void;
	/** Show all jobs on the map */
	onShowAll: () => void;
	/** Hide all jobs on the map */
	onHideAll: () => void;
	/** Handle delete button click for a job (optional - hides delete buttons when not provided) */
	onDeleteJob?: (jobId: string) => void;
	/** Get list of source files for a job */
	getJobSourceFiles: (jobId: string) => string[];
	/** Handle merge button click (optional - hides merge button when not provided) */
	onMerge?: () => void;
	/** Whether merge is enabled */
	canMerge?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Displays a panel with affected jobs for wizard issue resolution steps.
 * Includes visibility toggles, color swatches matching map colors, and delete buttons.
 */
export function AffectedJobsPanel({
	jobs,
	hiddenJobIds,
	onToggleVisibility,
	onShowAll,
	onHideAll,
	onDeleteJob,
	getJobSourceFiles,
	onMerge,
	canMerge,
}: AffectedJobsPanelProps) {
	const panelRef = useRef<HTMLDivElement>(null);
	const [visibilityCompact, setVisibilityCompact] = useState(false);
	const [mergeCompact, setMergeCompact] = useState(false);

	useEffect(() => {
		const el = panelRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			const w = entry.contentRect.width;
			setVisibilityCompact(w <= 500);
			setMergeCompact(w <= 400);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const showAllButton = (
		<Button
			className="visibility-button"
			size="small"
			variant="text"
			onClick={onShowAll}
			disabled={hiddenJobIds.size === 0}
			startIcon={<Visibility />}
		>
			<span className="button-label visibility-label">Show All</span>
		</Button>
	);
	const hideAllButton = (
		<Button
			className="visibility-button"
			size="small"
			variant="text"
			onClick={onHideAll}
			disabled={hiddenJobIds.size === jobs.length}
			startIcon={<VisibilityOff />}
		>
			<span className="button-label visibility-label">Hide All</span>
		</Button>
	);
	const mergeButton = onMerge ? (
		<Button
			className="merge-button"
			size="small"
			variant="contained"
			startIcon={<CallMerge />}
			onClick={onMerge}
			disabled={!canMerge}
			data-testid="merge-jobs-button"
		>
			<span className="button-label merge-label">Merge ({jobs.length})</span>
		</Button>
	) : null;

	return (
		<Box className="affected-jobs-panel" ref={panelRef}>
			{/* Header */}
			<Box className="legend-header">
				<Typography variant="subtitle2">Affected Jobs ({jobs.length})</Typography>
				<Box className="legend-controls">
					{visibilityCompact ? <PortalTooltip title="Show All">{showAllButton}</PortalTooltip> : showAllButton}
					{visibilityCompact ? <PortalTooltip title="Hide All">{hideAllButton}</PortalTooltip> : hideAllButton}
					{mergeButton &&
						(mergeCompact ? (
							<PortalTooltip title={`Merge ${jobs.length} jobs`}>{mergeButton}</PortalTooltip>
						) : (
							mergeButton
						))}
				</Box>
			</Box>

			{/* Job List */}
			<Box className="job-list">
				{jobs.map((job, index) => {
					const isVisible = !hiddenJobIds.has(job.id);
					const color = BOUNDARY_COLORS[index % BOUNDARY_COLORS.length];
					const sourceFiles = getJobSourceFiles(job.id);
					const pointsCount = job.files?.pointsGeoJSON?.features?.length ?? 0;
					const zonesCount = job.files?.zonesGeoJSON?.features?.length ?? 0;

					return (
						<Box
							key={job.id}
							className={`job-item ${isVisible ? "" : "hidden"}`}
							sx={{
								borderBottom: index < jobs.length - 1 ? 1 : 0,
								borderColor: "divider",
							}}
						>
							{/* Color Swatch */}
							<Box
								className={`color-swatch ${isVisible ? "" : "hidden"}`}
								sx={{
									bgcolor: color,
									borderColor: isVisible ? color : undefined,
								}}
							/>

							{/* Visibility Toggle */}
							<PortalTooltip title={isVisible ? "Hide on map" : "Show on map"}>
								<IconButton size="small" onClick={() => onToggleVisibility(job.id)} className="visibility-toggle">
									{isVisible ? (
										<Visibility fontSize="small" color="primary" />
									) : (
										<VisibilityOff fontSize="small" color="disabled" />
									)}
								</IconButton>
							</PortalTooltip>

							{/* Job Info */}
							<Box className="job-info">
								<Typography variant="body2" fontWeight={500} noWrap>
									{job.growerName || job.importGrowerName}
								</Typography>
								<Typography variant="body2" color="text.secondary" noWrap>
									{job.farmName || job.importFarmName} &bull; {job.fieldName || job.importFieldName}
								</Typography>
								{/* Source Files */}
								{sourceFiles.length > 0 && (
									<Box className="source-files">
										<InsertDriveFileOutlined className="source-files-icon" />
										<Typography variant="caption" color="text.secondary" noWrap>
											{sourceFiles.join(", ")}
										</Typography>
									</Box>
								)}
								{/* Points/Zones Counts */}
								{(pointsCount > 0 || zonesCount > 0) && (
									<Box className="geo-counts">
										{pointsCount > 0 && (
											<Box className="geo-count-badge">
												<ScatterPlot className="geo-count-icon" />
												<Typography variant="caption" color="text.secondary">
													{pointsCount} {pointsCount === 1 ? "point" : "points"}
												</Typography>
											</Box>
										)}
										{zonesCount > 0 && (
											<Box className="geo-count-badge">
												<GridOn className="geo-count-icon" />
												<Typography variant="caption" color="text.secondary">
													{zonesCount} {zonesCount === 1 ? "zone" : "zones"}
												</Typography>
											</Box>
										)}
									</Box>
								)}
							</Box>

							{/* Delete Button (only shown when onDeleteJob is provided) */}
							{onDeleteJob && (
								<PortalTooltip title="Delete this job">
									<IconButton
										data-testid={`delete-job-button-${job.id}`}
										size="small"
										color="error"
										onClick={() => onDeleteJob(job.id)}
									>
										<Delete fontSize="small" />
									</IconButton>
								</PortalTooltip>
							)}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
